// Sales · Accounts — account-centric customer list with the Direct/Reseller
// marker and real per-org aggregates (license counts + MRR). Row → account
// detail (the licensing hub). Mirrors portal/sales.jsx SalesAccounts.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { PageHeader, Card, Table, Td, Row, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { money, moneyK } from '@/lib/format';
import { PERSONA, personaBase } from '@/lib/persona';
import { getOrganizations, getFleetAllocationSummaries, type FleetAllocationSummary, type OrgRow } from '@/lib/api/accounts';
import { TrialCountdown } from '@/components/trial/TrialCountdown';
import { useCurrentUser } from '@/context/PersonaProvider';

const SALES = PERSONA.sales.accent;

function OrgAvatar({ name }: { name: string }) {
  const initials = name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg-muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', flexShrink: 0 }}>{initials}</span>;
}

export function SalesAccounts() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = personaBase(location.pathname);
  const isAdmin = useCurrentUser()?.isAdmin === true;
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allocationError, setAllocationError] = useState<string | null>(null);
  const [allocations, setAllocations] = useState<Map<string, FleetAllocationSummary>>(new Map());
  const [sort, setSort] = useState<'mrr' | 'name'>('mrr');
  const [q, setQ] = useState('');

  useEffect(() => {
    getOrganizations()
      .then(setOrgs)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load accounts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setAllocations(new Map());
    setAllocationError(null);
    if (!isAdmin) return;
    let cancelled = false;
    getFleetAllocationSummaries()
      .then((rows) => {
        if (cancelled) return;
        const byAccount = new Map<string, FleetAllocationSummary>();
        for (const row of rows) {
          if (row.organization_id && !byAccount.has(row.organization_id)) byAccount.set(row.organization_id, row);
        }
        setAllocations(byAccount);
      })
      .catch((e) => {
        if (!cancelled) setAllocationError(e instanceof Error ? e.message : 'Failed to load allocation indicators');
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const rows = useMemo(() => {
    let list = orgs.filter((o) => o.active !== false);
    if (q) list = list.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));
    return [...list].sort((a, b) => (sort === 'mrr' ? (b.mrr || 0) - (a.mrr || 0) : a.name.localeCompare(b.name)));
  }, [orgs, sort, q]);

  const totalMrr = orgs.reduce((s, o) => s + (o.mrr || 0), 0);

  return (
    <div>
      <PageHeader
        title="Accounts"
        subtitle={`${orgs.length} customer accounts · ${moneyK(totalMrr)} total MRR`}
        actions={<><Btn icon="download" disabled title="Accounts export is not yet supported.">Export</Btn><Btn kind="primary" accent={SALES} icon="plus" onClick={() => navigate('/sales/accounts/new')}>New Account</Btn></>}
      />
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
          <Icon name="search" size={14} color="var(--text-muted)" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…" style={{ border: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', flex: 1, color: 'var(--text-primary)', background: 'transparent' }} />
        </div>
        {(['mrr', 'name'] as const).map((k) => (
          <button key={k} onClick={() => setSort(k)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: sort === k ? 'var(--bg-muted)' : 'var(--surface)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: sort === k ? 'var(--text-primary)' : 'var(--text-secondary)' }}>Sort: {k === 'mrr' ? 'MRR' : 'Name'}</button>
        ))}
      </div>

      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{error}</AlertBanner></div>}
      {allocationError && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{allocationError}</AlertBanner></div>}

      <Card style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading accounts…</div>
        ) : (
          <Table cols={[{ label: 'Account' }, { label: 'Relationship' }, { label: 'Licenses', align: 'right' }, { label: 'Active', align: 'right' }, { label: 'MRR', align: 'right' }, { label: '', align: 'right' }]}>
            {rows.map((a) => (
              <Row key={a.id} onClick={() => navigate(`${base}/accounts/${a.id}`)}>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <OrgAvatar name={a.name} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{a.name}</div>
                      <TrialCountdown isTrial={allocations.get(a.id)?.is_trial} expiresAt={allocations.get(a.id)?.trial_expires_at} />
                      {a.domain && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{a.domain}</div>}
                    </div>
                  </div>
                </Td>
                <Td>{allocations.get(a.id)?.org_kind === 'supplier'
                  ? <Pill bg="var(--bg-muted)" fg="var(--text-secondary)">Supplier</Pill>
                  : a.org_kind === 'tenant' ? <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">Tenant</Pill> : a.customer_kind === 'reseller' ? <Pill bg="var(--ps-pill-purple-bg)" fg="var(--ps-pill-purple-tx)">Reseller</Pill> : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Direct</span>}</Td>
                <Td align="right"><span style={{ fontWeight: 600 }}>{a.license_count ?? 0}</span></Td>
                <Td align="right"><span style={{ color: 'var(--text-secondary)' }}>{a.active_licenses ?? 0}</span></Td>
                <Td align="right" style={{ fontWeight: 600 }}>{a.mrr ? money(a.mrr) : '—'}</Td>
                <Td align="right"><Icon name="chevron-right" size={16} color="var(--text-muted)" /></Td>
              </Row>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

export default SalesAccounts;
